import { computed, type ComputedRef, ref, type Ref, watch } from 'vue'
import type { RefOrGetter } from '@/helpers'
import { getTimeout } from '@/helpers/globals.ts'

export interface Clicker {
    bound: Ref<boolean>
    hasClick: ComputedRef<boolean>
}

interface DoubleClickerOptions {
    container: RefOrGetter<HTMLElement | null>
    delay?: number
    onClick?: () => void
    onFirstClick?: () => void
    onDoubleClick?: () => void
}

export function useDoubleClicker(options: DoubleClickerOptions): Clicker {
    const withDefaults: Required<DoubleClickerOptions> = {
        onClick: () => {},
        onFirstClick: () => {},
        onDoubleClick: () => {},
        delay: 200,
        ...options,
    }

    const {
        container: containerRef,
        onClick,
        onFirstClick,
        onDoubleClick,
        delay,
    } = withDefaults

    const bound = ref(false)

    const hasClick = ref(false)
    let timeoutId: number = 0

    const timeout = getTimeout()

    function onClickEvent(e: MouseEvent) {
        e.preventDefault()

        if (timeout) {
            const { setTimeout, clearTimeout } = timeout

            clearTimeout(timeoutId)

            if (hasClick.value) {
                onDoubleClick()
                hasClick.value = false
            } else {
                hasClick.value = true
                onFirstClick()

                timeoutId = setTimeout(() => {
                    onClick()
                    hasClick.value = false
                }, delay)
            }
        } else {
            onClick()
        }
    }

    watch(containerRef, (container, oldContainer) => {
        if (container) {
            container.addEventListener('click', onClickEvent)
        }

        bound.value = !!container

        if (oldContainer) {
            oldContainer.removeEventListener('click', onClickEvent)
        }
    })

    return {
        bound,
        hasClick: computed(() => hasClick.value),
    }
}
